import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from 'react'
import { useSearchParams } from 'react-router-dom'

interface Overlays {
  setHasViewedSkipSurvey(show: boolean): void
  hasViewedSkipSurvey: boolean
  isMenuTutorialSkipMultipleOpen: boolean
  isMenuTutorialSkipSingleOpen: boolean
  isRatingDialogOpen: boolean
  setIsMenuTutorialSkipMultipleOpen(isOpen: boolean): void
  setIsMenuTutorialSkipSingleOpen(isOpen: boolean): void
  setIsReferralModalOpen(isOpen: boolean): void
  isReferralModalOpen: boolean
  trackRatingDialogOpen(): () => void
}

const OverlaysContext = createContext<Overlays | undefined>(undefined)

const OverlaysProvider = ({
  children,
}: {
  children: ReactNode
}): JSX.Element => {
  const [searchParams] = useSearchParams()

  const [hasViewedSkipSurvey, setHasViewedSkipSurvey] = useState(false)
  const [isMenuTutorialSkipMultipleOpen, setIsMenuTutorialSkipMultipleOpen] =
    useState(false)
  const [isMenuTutorialSkipSingleOpen, setIsMenuTutorialSkipSingleOpen] =
    useState(false)
  const [isReferralModalOpen, setIsReferralModalOpen] = useState(() => {
    const referralSearchParam = searchParams.get('referral')

    return !!(referralSearchParam === 'true')
  })
  const [openRatingDialogCount, setOpenRatingDialogCount] = useState(0)

  const trackRatingDialogOpen = useCallback(() => {
    setOpenRatingDialogCount((count) => count + 1)

    return () => {
      setOpenRatingDialogCount((count) => count - 1)
    }
  }, [])

  return (
    <OverlaysContext.Provider
      value={{
        setHasViewedSkipSurvey,
        hasViewedSkipSurvey,
        isMenuTutorialSkipMultipleOpen,
        isMenuTutorialSkipSingleOpen,
        isRatingDialogOpen: openRatingDialogCount > 0,
        setIsMenuTutorialSkipMultipleOpen,
        setIsMenuTutorialSkipSingleOpen,
        setIsReferralModalOpen,
        isReferralModalOpen,
        trackRatingDialogOpen,
      }}
    >
      {children}
    </OverlaysContext.Provider>
  )
}

function useOverlays() {
  const context = useContext(OverlaysContext)
  if (context === undefined) {
    throw new Error('useOverlays must be used in an OverlaysProvider')
  }

  return context
}

export { OverlaysProvider, useOverlays }
